import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { MdOutlineMedicalServices } from "react-icons/md";
import { Language } from "../../../App";
import Avatar from "../../../assets/images/icon/placeholder_Awatar.png";
import { _imagesURL, FollowApi } from "../../../constants/API";
import ProfileSuggestionModal from "../../modals/ProfileSuggestionModal";
import { authorize } from "../../../shared/authorize";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ImageComponent from "../../common/ImageComponent";
import ProfileAvatar from "../../common/ProfileAvatar";
import { getUserSubtitle, navigateToProfile } from "../../../utils/profileNavigator";
import "./suggestedDoctors.css";

export default function ProfileSuggestionSlider({ suggestions = [], updateSuggestions }) {
  const navigate = useNavigate();
  const userToken = JSON.parse(localStorage.getItem("SignupData"));
  const user_id = userToken?.data?.id;
  const [suggestionsData, setSuggestionsData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const scrollRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);
  const hasDragged = useRef(false);

  const { lang } = useContext(Language);

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, []);

  const handleFollow = useCallback(
    (id) => {
      setIsLoading(true);
      axios
        .get(`${FollowApi}${id}`, authorize())
        .then((res) => {
          if (res.data.status) {
            const updater = (prevSuggestions) =>
              prevSuggestions.map((profile) =>
                profile.id === id
                  ? { ...profile, follow: !profile.follow }
                  : profile
              );
            setSuggestionsData(updater);
            if (updateSuggestions) {
              updateSuggestions(updater);
            }
          } else {
            toast.error(res.data.error);
          }
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [user_id, updateSuggestions]
  );

  useEffect(() => {
    if (suggestions.length > 0) {
      setSuggestionsData(suggestions);
    }
  }, [suggestions]);

  /* Mouse Drag-to-Scroll Support */
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    hasDragged.current = false;
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeftStart.current = scrollRef.current.scrollLeft;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const distance = (x - startX.current) * 1.25;
    if (Math.abs(distance) > 5) {
      hasDragged.current = true;
    }
    scrollRef.current.scrollLeft = scrollLeftStart.current - distance;
  };

  const handleMouseUpOrLeave = () => {
    isDragging.current = false;
  };

  const handleCardClick = (profile) => {
    if (hasDragged.current) return;
    navigateToProfile(profile, navigate);
  };

  if (!suggestionsData || suggestionsData.length === 0) {
    return null;
  }

  return (
    <>
      <ProfileSuggestionModal
        preview={modalOpen}
        updatePreview={toggleModal}
        list={suggestionsData}
        updateList={(newList) => {
          setSuggestionsData(newList);
          if (updateSuggestions) updateSuggestions(newList);
        }}
        toFollow={handleFollow}
        loading={isLoading}
      />

      <div className="sd-carousel-card">
        {/* Subtle Ambient Cyan Glow Orb */}
        <div className="sd-ambient-glow" />

        {/* Sophisticated Header Bar */}
        <div className="sd-header">
          <div className="sd-header-left">
            <div className="sd-icon-badge">
              <MdOutlineMedicalServices size={15} />
            </div>
            <div className="d-flex align-items-center gap-2">
              <h4 className="sd-title">
                {lang?.people_suggestions || lang?.suggested_doctors || "Suggested Profiles"}
              </h4>
              <span className="sd-subtitle-tag d-sm-inline-block d-none">
                Medical Network
              </span>
            </div>
          </div>

          <button
            type="button"
            className="sd-view-all"
            onClick={toggleModal}
          >
            <span>{lang?.view_all_recommendations || "View All"}</span>
            <span style={{ fontSize: "14px", lineHeight: "1" }}>→</span>
          </button>
        </div>

        {/* Full-Width Horizontal Scroller */}
        <div className="sd-carousel-viewport">
          <div
            ref={scrollRef}
            className="sd-scroll-track"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUpOrLeave}
            onMouseLeave={handleMouseUpOrLeave}
          >
            {suggestionsData.map((profile) => (
              <div key={profile.id} className="sd-doctor-card">
                {/* Avatar with cyan ring, inner glow & verified badge */}
                <div
                  className="sd-avatar-wrap"
                  onClick={() => handleCardClick(profile)}
                >
                  <ProfileAvatar
                    src={profile.image}
                    size={88}
                    variant="plain"
                    alt={profile.name || "Specialist"}
                  />
                  <div className="sd-verified-badge" title="Verified Medical Specialist">
                    <RiVerifiedBadgeFill />
                  </div>
                </div>

                {/* Profile Credentials */}
                <div
                  className="sd-info"
                  onClick={() => handleCardClick(profile)}
                >
                  <h5 className="sd-name" title={profile.name}>
                    {profile.name}
                  </h5>
                  <p
                    className="sd-specialty"
                    title={getUserSubtitle(profile, lang)}
                  >
                    {getUserSubtitle(profile, lang)}
                  </p>
                </div>

                {/* Follow / Following CTA */}
                <div className="w-100">
                  {String(profile.id) !== String(user_id) && (
                    <button
                      type="button"
                      disabled={isLoading}
                      className={`sd-follow-btn ${
                        profile.follow
                          ? "sd-follow-btn--following"
                          : "sd-follow-btn--follow"
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFollow(profile.id);
                      }}
                    >
                      <span>
                        {profile.follow
                          ? lang?.following || "Following"
                          : `+ ${lang?.follow || "Follow"}`}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
